<script setup lang="ts">
import HomeLayout from "~/layouts/HomeLayout.vue"
import { onMounted } from "vue"
import type { Category } from "~/models/Category"
import { useOFetch } from "~/composables/useOFetch"
import CardLanding from "~/components/CardLanding.vue"
import OfferProductsLanding from "~/components/OfferProductsLanding.vue"
import MoreProducts from "~/components/MoreProducts.vue"
import MoreStores from "~/components/MoreStores.vue"
import CategoriesSection from "~/components/CategoriesSection.vue"
import HomeSeoSection from "~/components/HomeSeoSection.vue"
import { useProductCardData } from "~/composables/ProductCardData"

/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const SEO_TITLE = "Tienda Online para Envíos a Cuba | Alimentos, Hogar y más."
const SEO_DESCRIPTION =
    "Dapamas, tu tienda online para comprar desde cualquier país, y tu familia lo recibe en Cuba. Comida, hogar y mucho más al mejor precio. Pago seguro."

useHead({
    title: SEO_TITLE,
    titleTemplate: "%s",
})

useSeoMeta({
    description: SEO_DESCRIPTION,
    ogTitle: SEO_TITLE,
    ogDescription: SEO_DESCRIPTION,
})

const shopStore = useShopListStore()
const storesData = computed(() => shopStore.shopList)
const { selectedProvince } = useProvinceSelection()
const { $bus } = useNuxtApp()

const {
    data: bestSellingProducts,
    error: bestSellingProductsError,
    loading: bestSellingProductsLoading,
    get: getBestSellingProducts,
} = useOFetchCustom("public/best_sellers")

const {
    data: offerProducts,
    error: offerProductsError,
    loading: offerProductsLoading,
    get: getOfferProducts,
} = useOFetchCustom("public/products_interest")

const {
    data: landingProducts,
    error: landingProductsError,
    loading: landingProductsLoading,
    get: getLandingProducts,
} = useOFetchCustom("public/home_info")

const { data: allStores, get: getAllStores } =
    useOFetchCustom("public/all_stores")

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(async () => {
    const header = {
        province: selectedProvince.value ?? null,
    }
    getBestSellingProducts()
    getOfferProducts(header)
    getLandingProducts()
    getAllStores()

    $bus.$on("update:product-list", (params: any) => {
        // Directly reload products instead of calling onQueryChange
        getOfferProducts(header)
    })
})

onUnmounted(async () => {
    $bus.$off("update:product-list")
})

/**
 *
 *  Functions section
 *
 *
 */

const { add: addToCart } = useProductCardData({} as any)

function addInterestProductToCart() {
    const product = offerProducts.value?.data?.data?.[0]
    if (product) {
        addToCart(product, 1)
    }
}

async function goToStore(category?: Category) {
    if (category) {
        await useOFetch("GET", "/store", { category: category.id })
    } else {
        await useOFetch("GET", "/store", null)
    }
}
</script>
<template>
    <HomeLayout withoutBreadCrumbs parentClass="!tw-py-0">
        <template #banner>
            <HomeCarouselSection
                title="Mejores precios"
                subtitle=" en todos nuestros productos"
            />
        </template>
        <template #pre_main>
            <div
                v-if="
                    landingProducts?.data &&
                    landingProducts?.data.new_product &&
                    landingProducts?.data &&
                    landingProducts?.data.most_sold_product
                "
                class="tw-hidden xl:tw-grid xl:tw-grid-cols-12 xl:tw-gap-5 md:tw-flex tw-flex-col tw-items-center xl:tw-pt-20 max-container"
            >
                <CardLanding
                    v-if="
                        landingProducts?.data &&
                        landingProducts?.data.new_product
                    "
                    class="xl:tw-col-span-6 fadeIn-normal tw-h-[398px] xl:tw-h-full"
                    variant="primary"
                    title="Acabado de llegar"
                    :subtitle="landingProducts.data.new_product.name"
                    :image="
                        landingProducts.data.new_product.images[0]?.origin ?? ''
                    "
                    :product="landingProducts?.data.new_product"
                />
                <CardLanding
                    v-if="
                        landingProducts?.data &&
                        landingProducts?.data.most_sold_product
                    "
                    class="xl:tw-col-span-6 fadeIn-normal tw-h-[398px] xl:tw-h-full"
                    variant="secondary"
                    title="LO MÁS VENDIDO"
                    :subtitle="landingProducts.data.most_sold_product.name"
                    :image="
                        landingProducts.data.most_sold_product.images[0]
                            ?.origin ?? ''
                    "
                    :product="landingProducts?.data.most_sold_product"
                />
            </div>
        </template>
        <div
            class="max-container"
            v-if="allStores?.data && allStores?.data?.length > 0"
        >
            <MoreStores
                subtitle="Todo lo que puedas encontrar"
                title="Descúbrelo en nuestras tiendas"
                subtitleClass="!tw-text-base !tw-font-semibold"
                titleClass="!tw-text-2xl !tw-font-semibold"
                :stores="allStores?.data"
                v-if="allStores?.data?.length > 0"
            />
        </div>
        <OfferProductsLanding
            title="Aquí están nuestras ofertas"
            subtitle="No pierdas la oportunidad!"
            subtitleClass="!tw-text-base !tw-font-semibold"
            titleClass="!tw-text-2xl !tw-font-semibold"
            :data="offerProducts?.data?.data || []"
            v-if="
                offerProducts?.data?.data?.length > 0 && !offerProductsLoading
            "
        />

        <template #post_main>
            <CategoriesSection />

            <!-- Mobile Only - Featured Product Banner -->
            <div
                class="md:tw-hidden tw-overflow-hidden tw-px-4 tw-pt-12 tw-pb-6"
                v-if="offerProducts?.data?.data?.length > 0"
            >
                <div class="tw-relative">
                    <!-- Orange card -->
                    <div
                        class="tw-bg-secondary tw-p-6 tw-pb-8 tw-flex tw-flex-col tw-justify-between tw-gap-6 tw-w-[260px] tw-h-[260px]"
                    >
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <p
                                class="tw-text-white tw-font-bold tw-text-xl tw-uppercase tw-font-titilium tw-leading-tight"
                            >
                                ACABADO DE LLEGAR
                            </p>
                            <p
                                class="tw-text-primary tw-font-semibold tw-text-base tw-font-titilium"
                            >
                                {{
                                    offerProducts.data.data[0].store_name ?? ""
                                }}
                            </p>
                            <p
                                class="tw-text-white tw-text-sm tw-font-titilium tw-leading-5 tw-mt-2"
                            >
                                {{ offerProducts.data.data[0].name }}
                            </p>
                        </div>
                        <button
                            class="tw-bg-white tw-text-secondary tw-font-bold tw-text-sm tw-px-8 tw-py-3 tw-rounded-full tw-w-max"
                            @click.stop="addInterestProductToCart"
                        >
                            Comprar ahora
                        </button>
                    </div>

                    <!-- Product image overflowing right -->
                    <NuxtImg
                        v-if="offerProducts.data.data[0].images[0]?.origin"
                        :src="offerProducts.data.data[0].images[0].origin"
                        alt="Producto destacado"
                        format="png"
                        class="tw-absolute tw-right-[-10px] tw-top-1/2 tw--translate-y-1/2 tw-w-[40%] tw-max-w-[290px] tw-h-[220px] tw-object-contain"
                    />
                </div>
            </div>

            <div
                class="tw-bg-customGray-200 tw-w-full xl:tw-max-h-[540px] tw-py-16"
                v-if="false"
            >
                <div
                    class="max-container tw-w-full tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center lg:tw-justify-between tw-gap-8"
                >
                    <div class="tw-pr-4">
                        <h2
                            class="tw-font-titilium tw-text-primary tw-text-2xl tw-font-semibold"
                        >
                            Testimonios
                        </h2>
                        <p
                            class="tw-font-titilium tw-text-customGray-700 tw-text-[32px] tw-font-bold md:tw-w-[376px]"
                        >
                            Descubre qué dicen nuestros clientes
                        </p>
                    </div>

                    <div
                        class="tw-flex tw-flex-col xl:tw-grid xl:tw-grid-cols-2 md:tw-flex-row tw-gap-16 md:tw-gap-4 tw-py-4 tw-pl-4"
                    >
                        <div class="tw-flex tw-flex-col tw-gap-16">
                            <div
                                class="tw-flex-1 xl:tw-max-w-[520px] xl:tw-max-h-[296px] tw-bg-white tw-flex tw-justify-center tw-items-center quote-container"
                            >
                                <q
                                    class="tw-font-semibold tw-text-xl tw-font-titilium tw-text-customGray-500 xl:tw-max-w-[455px] tw-py-8 tw-px-4 xl:tw-my-20 xl:tw-mx-10 tw-text-justify"
                                    >Mi experiencia de compra fue excelente. El
                                    proceso fue rápido y fácil, y el envío llegó
                                    a tiempo y en perfectas condiciones.
                                    ¡Totalmente recomendable!</q
                                >
                            </div>
                            <span
                                class="tw-text-2xl tw-font-semibold tw-font-titilium tw-text-customGray-700 tw-mx-auto"
                                >Alessandra Perez</span
                            >
                        </div>

                        <div class="tw-flex tw-flex-col tw-gap-16">
                            <div
                                class="tw-flex-1 xl:tw-max-w-[520px] xl:tw-max-h-[296px] tw-bg-white tw-flex tw-justify-center tw-items-center quote-container"
                            >
                                <q
                                    class="tw-font-semibold tw-text-xl tw-font-titilium tw-text-customGray-500 xl:tw-max-w-[455px] tw-py-8 tw-px-4 xl:tw-my-20 xl:tw-mx-10 tw-text-justify"
                                    >Todo fue muy satisfactorio: desde la compra
                                    hasta el envío. El servicio fue rápido y
                                    eficiente, y el producto llegó en perfectas
                                    condiciones. ¡Sin duda, volveré a
                                    comprar!</q
                                >
                            </div>
                            <span
                                class="tw-text-2xl tw-font-semibold tw-font-titilium tw-text-customGray-700 tw-mx-auto"
                                >Anabeisy Gonzalez</span
                            >
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="max-container"
                v-if="
                    bestSellingProducts &&
                    bestSellingProducts.data &&
                    bestSellingProducts.data.length > 0
                "
            >
                <MoreProducts
                    title="Lo más vendido!"
                    subtitle="Lo que más gusta!"
                    subtitleClass="!tw-text-base !tw-font-semibold"
                    titleClass="!tw-text-2xl !tw-font-semibold"
                    :products="bestSellingProducts.data"
                    v-if="
                        bestSellingProducts.data.length > 0 &&
                        !bestSellingProductsLoading
                    "
                />
            </div>

            <HomeSeoSection />
        </template>
    </HomeLayout>
</template>
<style>
.quote-container {
    @apply tw-relative tw-z-20;
}

.quote-container::after {
    content: "";
    @apply tw-w-[40px] tw-h-[40px] tw-bg-white tw-absolute -tw-bottom-5 tw-mx-auto tw-rotate-45;
}
</style>
